import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, pairwise, withLatestFrom } from 'rxjs/operators';

import { PanelActions } from './panel.actions';
import { PanelSelectors } from './panel.selectors';
import { HelpService } from '../../features/help/services/help.service';
import { AppActions } from '../app/app.actions';
import { DealSelectors } from '../deal/deal.selectors';
import { RootState } from '../root-state.model';

@Injectable()
export class PanelEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly helpService = inject(HelpService);

  toggleViewerState$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(PanelActions.ToggleViewerState),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const panels = store.panel.activePanelViews;
        const isOpen = panels.includes(action.panel);

        if (isOpen && panels.length === 1) {
          return PanelActions.SetCalculatedViewerState({ state: panels });
        }

        return PanelActions.SetCalculatedViewerState({
          state: isOpen ? panels.filter((s) => s !== action.panel) : [...panels, action.panel],
        });
      }),
    ),
  );

  /**
   * Points contextual help at a newly-opened deal-page panel. Watching the open set rather than
   * the rail's toggle covers every origin — rail, tab catalog and tab activation all land here.
   */
  helpIdForOpenedPanel$: Observable<Action> = createEffect(() =>
    this.store.select(PanelSelectors.activePanels).pipe(
      pairwise(),
      map(([previous, current]) => current.filter((panel) => !previous.includes(panel))),
      // Several at once is a layout switch (preset, saved layout), not a request to read about one panel.
      filter((opened) => opened.length === 1),
      withLatestFrom(this.store.select(DealSelectors.deal)),
      // Leaving the deal page collapses the set back to its default, which also looks like an open.
      filter(([, deal]) => !!deal),
      map(([[panel]]) => this.helpService.getHelpIdByPanelId(panel)),
      filter((helpId) => !!helpId),
      map((helpId) => AppActions.SetHelpId({ helpId })),
    ),
  );
}
